import { MarkHearthArch as HearthMark } from "../components/brand/Marks";
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Toaster, toast } from "sonner";
import { Lock, Loader2, Users, ScrollText, Globe, ShieldCheck, FileText, AlertTriangle, Mail, Mic, KeyRound } from "lucide-react";
import { emergencyInfo, emergencyAccess, letterPhotoUrl, emergencyMediaUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { NoIndex } from "../components/Seo";

export default function EmergencyView() {
  const { linkId } = useParams();
  const [info, setInfo] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    emergencyInfo(linkId)
      .then(setInfo)
      .catch(() => setLoadErr("This access link is invalid."));
  }, [linkId]);

  const submit = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      const res = await emergencyAccess(linkId, code);
      setData(res);
      toast.success("Access granted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Incorrect code");
    } finally {
      setBusy(false);
    }
  };

  const Shell = ({ children }) => (
    <div
      className="min-h-screen bg-background"
      style={{
        backgroundImage: "radial-gradient(hsl(var(--foreground) / 0.03) 1px, transparent 1px)",
        backgroundSize: "22px 22px",
      }}
    >
      <Toaster position="top-center" richColors />
      <header className="border-b border-border/80 bg-background/85 backdrop-blur-md">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-xl bg-primary text-primary-foreground grid place-items-center">
            <HearthMark className="h-5 w-5" />
          </div>
          <div className="leading-none">
            <div className="font-serif text-xl font-semibold">HearthVault</div>
            <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground mt-0.5">
              Emergency Access
            </div>
          </div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 py-10">{children}</main>
    </div>
  );

  if (loadErr) {
    return (
      <Shell>
      <NoIndex title="Emergency access" />
        <div className="text-center py-20">
          <AlertTriangle className="h-10 w-10 mx-auto text-amber-500" />
          <h1 className="mt-4 font-serif text-2xl">{loadErr}</h1>
        </div>
      </Shell>
    );
  }

  if (!info) {
    return (
      <Shell>
        <div className="py-24 text-center text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin mx-auto" />
        </div>
      </Shell>
    );
  }

  if (info.revoked || info.expired) {
    return (
      <Shell>
        <div className="text-center py-20" data-testid="emergency-unavailable">
          <Lock className="h-10 w-10 mx-auto text-muted-foreground" />
          <h1 className="mt-4 font-serif text-2xl">
            This link is no longer {info.revoked ? "active" : "valid"}.
          </h1>
          <p className="mt-2 text-muted-foreground">
            Please contact the vault owner for renewed access.
          </p>
        </div>
      </Shell>
    );
  }

  if (!data) {
    return (
      <Shell>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-sm mx-auto text-center pt-10"
        >
          <div className="h-16 w-16 mx-auto rounded-2xl bg-primary text-primary-foreground grid place-items-center shadow-lg">
            <ShieldCheck className="h-8 w-8" />
          </div>
          <h1 className="mt-5 font-serif text-2xl">Hello, {info.recipient_name}</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            You've been granted emergency access to essential estate information. Enter the code you
            were given to continue.
          </p>
          <Input
            data-testid="emergency-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            inputMode="numeric"
            maxLength={6}
            placeholder="6-digit code"
            className="mt-6 text-center text-xl tracking-[0.4em] font-mono bg-card h-14"
          />
          <Button
            data-testid="emergency-access-btn"
            onClick={submit}
            disabled={busy}
            className="mt-4 w-full gap-2 bg-primary hover:bg-primary/90 h-11"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
            Unlock
          </Button>
          <p className="mt-4 text-xs text-muted-foreground">
            The vault owner is notified when this access is used.
          </p>
        </motion.div>
      </Shell>
    );
  }

  const { estate, documents } = data;
  const Card = ({ icon: Icon, title, children, testid }) => (
    <section data-testid={testid} className="bg-card border border-border rounded-2xl p-5">
      <h3 className="font-serif text-lg font-medium flex items-center gap-2 mb-3">
        <Icon className="h-4 w-4 text-primary" /> {title}
      </h3>
      {children}
    </section>
  );

  return (
    <Shell>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="emergency-hub" className="space-y-5">
        <div>
          <p className="text-xs uppercase tracking-widest font-mono text-primary">Emergency estate hub</p>
          <h1 className="font-serif text-3xl font-medium mt-1">Everything the family needs</h1>
        </div>

        <Card icon={Users} title="Trusted Contacts" testid="ev-contacts">
          {estate.contacts.length ? (
            <div className="space-y-2">
              {estate.contacts.map((c) => (
                <div key={c.id || `${c.name}-${c.email}-${c.phone}`} className="text-sm border-b border-border last:border-0 pb-2">
                  <span className="font-medium">{c.name}</span>
                  {c.role && <span className="text-muted-foreground"> — {c.role}</span>}
                  <div className="text-muted-foreground text-xs font-mono">
                    {[c.phone, c.email].filter(Boolean).join(" · ")}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No contacts recorded.</p>
          )}
        </Card>

        <Card icon={ScrollText} title="Final Wishes" testid="ev-wishes">
          <div className="space-y-2 text-sm">
            {[
              ["Funeral & burial", estate.wishes.funeral],
              ["Organ donation", estate.wishes.organ_donation],
              ["Messages", estate.wishes.messages],
              ["Assets & accounts", estate.wishes.asset_notes],
            ].map(([label, val]) => (
              <div key={label}>
                <span className="font-medium">{label}:</span>{" "}
                <span className="text-muted-foreground">{(val || "").trim() || "—"}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card icon={ShieldCheck} title="Key Documents Checklist" testid="ev-checklist">
          <div className="space-y-1.5 text-sm">
            {estate.checklist.map((it) => (
              <div key={it.id || it.label} className="flex items-center gap-2">
                <span className={it.done ? "text-emerald-600" : "text-muted-foreground"}>
                  {it.done ? "✓" : "○"}
                </span>
                <span>{it.label}</span>
                {it.linked_title && (
                  <span className="text-xs text-muted-foreground font-mono">→ {it.linked_title}</span>
                )}
              </div>
            ))}
          </div>
        </Card>

        {data.access_codes?.length > 0 && (
          <Card icon={KeyRound} title="Access codes" testid="ev-access-codes">
            <div className="space-y-2 text-sm">
              {data.access_codes.map((c) => (
                <div key={c.id} className="flex flex-wrap items-baseline gap-x-3" data-testid={`ev-code-${c.id}`}>
                  <span className="font-medium">{c.label}</span>
                  <span className="text-xs text-muted-foreground">{c.kind}{c.property ? ` · ${c.property}` : ""}{c.network ? ` · network ${c.network}` : ""}{c.company ? ` · ${c.company} ${c.phone || ""}` : ""}</span>
                  <span className="font-mono tracking-[0.25em] ml-auto">{c.code}</span>
                  {c.notes && <span className="basis-full text-xs text-muted-foreground">{c.notes}</span>}
                </div>
              ))}
            </div>
          </Card>
        )}

        {estate.digital_accounts.length > 0 && (
          <Card icon={Globe} title="Digital Legacy" testid="ev-digital">
            <div className="space-y-2 text-sm">
              {estate.digital_accounts.map((a) => (
                <div key={a.id || `${a.service}-${a.instructions}`}>
                  <span className="font-medium">{a.service}</span>
                  {a.instructions && <span className="text-muted-foreground"> — {a.instructions}</span>}
                </div>
              ))}
            </div>
          </Card>
        )}

        {data.recorded_letters?.length > 0 && (
          <Card icon={Mic} title="Recorded letters" testid="ev-recorded-letters">
            <div className="space-y-5">
              {data.recorded_letters.map((l) => (
                <article key={l.id} className="text-sm border-b border-border last:border-0 pb-4" data-testid={`ev-recorded-${l.id}`}>
                  <div className="font-serif text-base font-medium">{l.to_name ? `To ${l.to_name} — ` : ""}{l.title}</div>
                  <div className="text-xs text-muted-foreground mb-2">Recorded {l.recorded_at}</div>
                  {l.content_type.startsWith("video/") ? (
                    <video controls preload="metadata" playsInline src={emergencyMediaUrl(l.id, data.hub_token)} className="w-full rounded-xl bg-black aspect-video" data-testid={`ev-recorded-video-${l.id}`} />
                  ) : (
                    <audio controls preload="metadata" src={emergencyMediaUrl(l.id, data.hub_token)} className="w-full" data-testid={`ev-recorded-audio-${l.id}`} />
                  )}
                  {l.transcript && <details className="mt-2"><summary className="cursor-pointer text-xs text-muted-foreground">Read the transcript</summary><p className="whitespace-pre-wrap mt-1 leading-relaxed">{l.transcript}</p></details>}
                </article>
              ))}
            </div>
          </Card>
        )}

        {data.letters?.length > 0 && (
          <Card icon={Mail} title="Letters left for the family" testid="ev-letters">
            <div className="space-y-4">
              {data.letters.map((l) => (
                <article key={l.id} className="text-sm border-b border-border last:border-0 pb-4" data-testid={`ev-letter-${l.id}`}>
                  <div className="font-serif text-base font-medium">To {l.to_name}{l.title ? ` — ${l.title}` : ""}</div>
                  <p className="whitespace-pre-wrap mt-1 leading-relaxed">{l.body}</p>
                  {l.photos?.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3" data-testid={`ev-letter-photos-${l.id}`}>
                      {l.photos.map((p) => <a key={p.id} href={letterPhotoUrl(l.id, p.id, data.hub_token)} target="_blank" rel="noreferrer" className="block rounded-lg overflow-hidden bg-muted aspect-square"><img src={letterPhotoUrl(l.id, p.id, data.hub_token)} alt={p.name} className="h-full w-full object-cover" /></a>)}
                    </div>
                  )}
                  {l.by && <div className="text-xs text-muted-foreground mt-2">— {l.by}</div>}
                </article>
              ))}
            </div>
          </Card>
        )}

        <Card icon={FileText} title={`Document Inventory (${documents.length})`} testid="ev-documents">
          <div className="space-y-1.5 text-sm">
            {documents.map((d) => (
              <div key={`${d.title}-${d.uploaded_at}`} className="flex items-center justify-between gap-3 border-b border-border last:border-0 pb-1.5">
                <span className="truncate">{d.title}</span>
                <span className="text-xs uppercase font-mono text-muted-foreground shrink-0">{d.category}</span>
              </div>
            ))}
          </div>
        </Card>
      </motion.div>
    </Shell>
  );
}
